// notifications.ts

import {
  Component,
  OnInit,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { OnboradingService } from '../../onboarding/shared/service/onboarding.service';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './notifications.html',
  styleUrl: './notifications.css',
})
export class Notifications implements OnInit {

  // =====================================
  // VARIABLES
  // =====================================

  notifications: any[] = [];

  selectedNotification: any = null;

  loading = false;

  showPopup = false;

  // =====================================
  // CONSTRUCTOR
  // =====================================
  private requestedNotificationSeq: number | null = null;

  constructor(
    private onboardingService: OnboradingService,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) {}

  // =====================================
  // INIT
  // =====================================

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const seq = params.get('notificationSeq');
      this.requestedNotificationSeq = seq ? +seq : null;
    });

    this.getAllNotifications();

  }

  // =====================================
  // GET ALL NOTIFICATIONS
  // =====================================

  getAllNotifications(): void {

    this.loading = true;

    this.onboardingService
      .getAllNotifications()
      .subscribe({

        next: (res: any) => {

          this.notifications =
            (res || []).map(
              (item: any) => ({

                notificationSeq:
                  item.notificationSeq,

                title:
                  item.title ||
                  this.translate.instant('NOTIFICATIONS.DEFAULT_TITLE'),

                message:
                  item.message ||
                  this.translate.instant('NOTIFICATIONS.NO_MESSAGE'),

                createdDate:
                  item.createdDate,

                notificationType:
                  item.notificationType,

                createdBy:
                  item.createdBy,

                role:
                  item.role,

                entitySeq:
                  item.entitySeq,

                entityType:
                  item.entityType,

                read:
                  item.readFlag,

                deleteFlag:
                  item.deleteFlag,

                pld:
                  item.pld,

              })
            );
          if (
            this.notifications.length > 0
          ) {
            const requested =
              this.requestedNotificationSeq !== null
                ? this.notifications.find(
                    (item) => item.notificationSeq === this.requestedNotificationSeq
                  )
                : null;
            this.selectedNotification =
              requested || this.notifications[0];

          }

          this.loading = false;

        },

        error: (err) => {

          console.error(err);

          this.loading = false;

        },

      });

  }

  // =====================================
  // SELECT NOTIFICATION
  // =====================================

  selectNotification(
    item: any
  ): void {

    this.selectedNotification =
      item;

    this.markAsRead(item);

  }

  // =====================================
  // MOBILE VIEW POPUP
  // =====================================

  viewDetails(
    item: any,
    event: Event
  ): void {

    event.stopPropagation();

    this.selectedNotification =
      item;

    this.showPopup = true;

    this.markAsRead(item);

  }

  // =====================================
  // CLOSE POPUP
  // =====================================

  closePopup(): void {

    this.showPopup = false;

  }

  // =====================================
  // MARK SINGLE READ
  // =====================================

  markAsRead(
    item: any
  ): void {

    if (item.read) {
      return;
    }

    const payload = {

      ntfctnSeq:
        item.notificationSeq,

      rdFlg: true,

      dltFlg: false,

    };

    this.onboardingService
      .markNotificationRead(payload)
      .subscribe({

        next: () => {

          item.read = true;

        },

        error: (err) => {

          console.error(err);

        },

      });

  }

  // =====================================
  // MARK ALL READ
  // =====================================

  markAllRead(): void {

    this.notifications.forEach(
      (item: any) => {

        if (!item.read) {

          this.markAsRead(item);

        }

      }
    );

  }

  // =====================================
  // GET NOTIFICATION ICON
  // =====================================

  getNotificationIcon(
    type: string
  ): string {

    switch (type) {

      case 'COMPLIANCE_ALERT':
        return 'shield';

      case 'WARNING':
        return 'warning';

      case 'SUCCESS':
        return 'success';

      default:
        return 'info';

    }

  }

  // =====================================
  // FORMAT DATE
  // =====================================

  formatDate(
    date: string
  ): string {

    if (!date) {
      return '';
    }

    return new Date(date)
      .toLocaleString();

  }

  // =====================================
  // ONLY THESE FIELDS ARE SHOWN IN
  // CUSTOMER DETAILS (IN THIS ORDER)
  // =====================================

  private customerFieldDefs: {
    label: string;
    aliases: string[];
  }[] = [

    {
      label: 'Full Name',
      aliases: [
        'flNm', 'fullName', 'custNm', 'clntNm', 'clientNm', 'name',
        'nmOfInstitution', 'cntctPrsnNm',
      ],
    },

    // FO letter notifications carry the document name in pld.docNm
    {
      label: 'Document Name',
      aliases: ['docNm'],
    },

    {
      label: 'Father Name',
      aliases: ['fthrHsbndNm', 'fthrNm', 'fatherName'],
    },

    {
      label: 'CNIC',
      aliases: ['cnicNum', 'cnicNo', 'cnic', 'cnc', 'nic'],
    },

    {
      label: 'Phone Number',
      aliases: ['phnNum', 'mblNum', 'mobileNumber', 'phoneNumber', 'contactNum'],
    },

    {
      label: 'Address',
      aliases: [
        'address', 'addr', 'prsntAddr', 'permntAddr', 'rsdntlAddr', 'mlngAddr',
        'regtrdAddrss', 'mlngAddrss',
      ],
    },

  ];

  // Display-time translation keys for customerFieldDefs labels
  private fieldLabelKeys: Record<string, string> = {
    'Full Name': 'NOTIFICATIONS.FULL_NAME',
    'Document Name': 'NOTIFICATIONS.DOCUMENT_NAME',
    'Father Name': 'NOTIFICATIONS.FATHER_NAME',
    'CNIC': 'NOTIFICATIONS.CNIC',
    'Phone Number': 'NOTIFICATIONS.PHONE_NUMBER',
    'Address': 'NOTIFICATIONS.ADDRESS',
  };

  getFieldLabelKey(label: string): string {
    return this.fieldLabelKeys[label] || label;
  }

  // =====================================
  // BUILD CUSTOMER DETAIL FIELDS
  // =====================================

  getPldFields(
    pld: any
  ): { key: string; label: string; value: any }[] {

    if (!pld) {
      return [];
    }

    const pldKeys = Object.keys(pld);

    const fields: { key: string; label: string; value: any }[] = [];

    this.customerFieldDefs.forEach(
      (def) => {

        const matchedKey = pldKeys.find(
          (key) =>
            def.aliases.some(
              (alias) => alias.toLowerCase() === key.toLowerCase()
            ) &&
            pld[key] !== null &&
            pld[key] !== undefined &&
            pld[key] !== ''
        );

        if (matchedKey) {

          fields.push({

            key: matchedKey,

            label: def.label,

            value: pld[matchedKey],

          });

        }

      }
    );

    return fields;

  }

}